/**
 * batch-66 verkoop-extractie (FO-493) - deterministische naverwerking van een
 * AI-extractie met de PDF-tekst. Puur: geen DB, geen AI, geen klok.
 *
 * De AI leest de PDF; deze stap legt daar de waarden uit de tekstlaag naast.
 * Wat de tekst met zekerheid zegt (factuurnummer, datums, totalen, partijen,
 * regels) WINT van de AI; wat alleen de AI gaf, blijft staan met een lagere
 * zekerheid. Het resultaat draagt per veld waar de waarde vandaan komt.
 *
 * Twee regels die altijd gelden, met of zonder profiel:
 *   - een factoring-IBAN is nooit het rekeningnummer van de klant of de BV;
 *   - "N/A" is geen btw-nummer.
 */

import type {
  ExtractieNaverwerker,
  ExtractieRegelParser,
  ExtractieVeld,
  ExtractieVeldZekerheid,
  ExtractieZekerheidPerVeld,
  GestructureerdeFactuurRegel,
} from '@streep/types'
import { normalizeBedrijfsnaam, normaliseerIbanSleutel } from '../eigen-bv-herkenning'
import {
  finqleEindtotalen,
  isLegeWaardeAanduiding,
  leesFinqleTekst,
  type FinqleTekstExtractie,
  type FinqleTekstRegel,
} from './finqle'
import {
  centenNaarGetal,
  duizendstenNaarGetal,
  getalNaarCenten,
  herstelSpaties,
  isoNaarNlDatum,
  normaliseerWitruimte,
  ontkernNaam,
  plet,
  woordenUit,
} from './tekst'

/** Een extractieregel (zelfde vorm als `ExtractedInvoiceLineFields` in de api). */
export interface ExtractieRegelBasis {
  omschrijving: string
  aantal: number | null
  prijsPerStuk: number | null
  totaal: number | null
  btwPercentage: number | null
}

/** De velden die de naverwerking leest en schrijft (de api-extractie voldoet hier structureel aan). */
export interface ExtractieVeldenBasis {
  factuurnummer: string | null
  debiteurnaam: string | null
  debiteurKvk: string | null
  bvNaam: string | null
  datum: string | null
  vervaldatum: string | null
  subtotaal: number | null
  btwBedrag: number | null
  btwPercentage: number | null
  totaal: number | null
  referentienummer: string | null
  omschrijving: string | null
  regels: ExtractieRegelBasis[] | null
  crediteurNaam: string | null
  crediteurKvk: string | null
  crediteurBtwNummer: string | null
  crediteurIban: string | null
  debiteurIban?: string | null
  debiteurBtwNummer?: string | null
}

/** Een rekeningnummer dat nooit van de klant of de BV is. */
export interface GeblokkeerdIban {
  iban: string
  /** Ten name van; staat die partij zelf als afzender op de factuur, dan mag het IBAN blijven. */
  tenNameVan: string | null
}

/** Een partij uit de PDF-tekst (voor de klant- en BV-herkenning). */
export interface NaverwerktePartij {
  naam: string | null
  kvk: string | null
  btwNummer: string | null
}

export interface NaverwerkingUitkomst<T extends ExtractieVeldenBasis> {
  velden: T
  zekerheid: ExtractieZekerheidPerVeld
  /** Gestructureerde regels (leeg zonder regel-parser of zonder tekst). */
  regels: GestructureerdeFactuurRegel[]
  markeringen: string[]
  /** IBAN's die uit de extractie zijn gehaald. */
  geblokkeerdeIbans: string[]
  /** Het factoring-IBAN dat in de betalingsinstructies staat (voor een instel-voorstel). */
  factoringIbanInTekst: GeblokkeerdIban | null
  debiteur: NaverwerktePartij | null
  afzender: NaverwerktePartij | null
  besteldDoor: string | null
  /** De tekstlezer heeft de opmaak herkend en gelezen. */
  tekstGelezen: boolean
}

export interface NaverwerkingOpties {
  /** De PDF-tekst (null = scan of niet leesbaar: dan geldt alleen de AI). */
  tekst: string | null
  naverwerker: ExtractieNaverwerker | null
  regelParsers?: readonly ExtractieRegelParser[]
  /** Ingestelde factoring-IBAN's (alle BV's samen). */
  factoringIbans?: readonly GeblokkeerdIban[]
  /** Bekende namen (medewerkers) om kerning-spaties te herstellen. */
  bekendeNamen?: readonly string[]
}

// ─── IBAN-blokkade ───────────────────────────────────────────────────────────

/**
 * Haal een factoring-IBAN uit de extractie. Het debiteur-IBAN vervalt altijd;
 * het crediteur-IBAN blijft alleen staan als de crediteur ZELF de partij is op
 * wiens naam het IBAN staat (een factuur VAN de factoringmaatschappij).
 */
export function pasIbanBlokkadeToe<T extends Pick<ExtractieVeldenBasis, 'crediteurIban' | 'debiteurIban' | 'crediteurNaam'>>(
  velden: T,
  geblokkeerd: readonly GeblokkeerdIban[],
): { velden: T; geblokkeerdeIbans: string[] } {
  if (geblokkeerd.length === 0) return { velden, geblokkeerdeIbans: [] }
  const opSleutel = new Map<string, GeblokkeerdIban>()
  for (const g of geblokkeerd) {
    const sleutel = normaliseerIbanSleutel(g.iban)
    if (sleutel && !opSleutel.has(sleutel)) opSleutel.set(sleutel, g)
  }
  const weg: string[] = []
  const uit = { ...velden }

  const debiteur = normaliseerIbanSleutel(velden.debiteurIban ?? null)
  if (debiteur && opSleutel.has(debiteur)) {
    uit.debiteurIban = null
    weg.push(debiteur)
  }
  const crediteur = normaliseerIbanSleutel(velden.crediteurIban)
  const regel = crediteur ? opSleutel.get(crediteur) : undefined
  if (crediteur && regel) {
    const eigenaar = regel.tenNameVan ? normalizeBedrijfsnaam(regel.tenNameVan) : ''
    const afzender = velden.crediteurNaam ? normalizeBedrijfsnaam(velden.crediteurNaam) : ''
    const afzenderIsEigenaar = eigenaar.length >= 3 && afzender === eigenaar
    if (!afzenderIsEigenaar) {
      uit.crediteurIban = null
      if (!weg.includes(crediteur)) weg.push(crediteur)
    }
  }
  return { velden: uit, geblokkeerdeIbans: weg }
}

/** "N/A" en soortgenoten zijn geen btw-nummer: maak er null van. */
export function schoonBtwNummer(waarde: string | null | undefined): string | null {
  if (waarde == null) return null
  const t = waarde.trim()
  return isLegeWaardeAanduiding(t) ? null : t
}

// ─── Zekerheid ───────────────────────────────────────────────────────────────

function gelijkeTekst(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return false
  return plet(a).toLowerCase() === plet(b).toLowerCase()
}

/** Kies tussen de tekstwaarde en de AI-waarde en leg vast waar de waarde vandaan komt. */
function kiesTekst(
  ai: string | null,
  tekst: string | null,
  tekstIsWaarheid: boolean,
): { waarde: string | null; zekerheid: ExtractieVeldZekerheid } {
  if (tekst !== null) {
    if (gelijkeTekst(ai, tekst)) {
      // De AI-schrijfwijze is leesbaarder (geen kerning); inhoudelijk gelijk.
      return { waarde: ai, zekerheid: { zekerheid: 'high', bron: 'TEKST_EN_AI' } }
    }
    return {
      waarde: tekst,
      zekerheid: { zekerheid: 'high', bron: 'TEKST', ...(ai !== null ? { aiWeekAf: true } : {}) },
    }
  }
  if (tekstIsWaarheid) {
    // Het label staat er, de waarde is leeg: dat is een feit (bv. "PO: -").
    return {
      waarde: null,
      zekerheid: { zekerheid: 'high', bron: 'TEKST', ...(ai !== null ? { aiWeekAf: true } : {}) },
    }
  }
  if (ai !== null) return { waarde: ai, zekerheid: { zekerheid: 'medium', bron: 'AI' } }
  return { waarde: null, zekerheid: { zekerheid: 'low', bron: 'GEEN' } }
}

/** Zeggen de tekst en de AI iets anders over het TEKEN van hetzelfde bedrag? */
function tekenVerschilt(ai: number | null, tekstCenten: number | null): boolean {
  if (ai === null || tekstCenten === null) return false
  const aiCenten = getalNaarCenten(ai)
  return aiCenten !== 0 && tekstCenten !== 0 && Math.sign(aiCenten) !== Math.sign(tekstCenten)
}

function kiesBedrag(
  ai: number | null,
  tekstCenten: number | null,
  betrouwbaar: boolean,
  /**
   * De tekst en de AI zijn het oneens over het teken van de bedragen (mogelijk
   * een creditfactuur). Dan is niets "zeker": het bedrag wordt negatief (er is
   * een aanwijzing voor credit) en de reviewer controleert.
   */
  tekenOnzeker: boolean,
): { waarde: number | null; zekerheid: ExtractieVeldZekerheid } {
  if (tekstCenten !== null) {
    if (tekenOnzeker) {
      return {
        waarde: centenNaarGetal(-Math.abs(tekstCenten)),
        zekerheid: {
          zekerheid: 'medium',
          bron: 'TEKST',
          ...(ai !== null ? { aiWeekAf: true } : {}),
          toelichting: 'De PDF-tekst en de AI-uitlezing verschillen in het teken van de bedragen.',
        },
      }
    }
    const gelijk = ai !== null && getalNaarCenten(ai) === tekstCenten
    return {
      waarde: centenNaarGetal(tekstCenten),
      zekerheid: {
        zekerheid: betrouwbaar ? 'high' : 'medium',
        bron: gelijk ? 'TEKST_EN_AI' : 'TEKST',
        ...(ai !== null && !gelijk ? { aiWeekAf: true } : {}),
        ...(betrouwbaar ? {} : { toelichting: 'De bedragen in de PDF-tekst sluiten niet.' }),
      },
    }
  }
  if (ai !== null) return { waarde: ai, zekerheid: { zekerheid: 'medium', bron: 'AI' } }
  return { waarde: null, zekerheid: { zekerheid: 'low', bron: 'GEEN' } }
}

/** Zekerheid van een extractie waar geen tekst naast gelegd kon worden. */
export function zekerheidZonderTekst(velden: ExtractieVeldenBasis): ExtractieZekerheidPerVeld {
  const veld = (aanwezig: boolean): ExtractieVeldZekerheid =>
    aanwezig ? { zekerheid: 'medium', bron: 'AI' } : { zekerheid: 'low', bron: 'GEEN' }
  return {
    factuurnummer: veld(velden.factuurnummer !== null),
    factuurdatum: veld(velden.datum !== null),
    vervaldatum: veld(velden.vervaldatum !== null),
    subtotaal: veld(velden.subtotaal !== null),
    btwBedrag: veld(velden.btwBedrag !== null),
    btwPercentage: veld(velden.btwPercentage !== null),
    totaal: veld(velden.totaal !== null),
    referentienummer: veld(velden.referentienummer !== null),
    omschrijving: veld(velden.omschrijving !== null),
    regels: veld((velden.regels?.length ?? 0) > 0),
  }
}

/** Velden die meetellen in de zekerheidsscore (vrije tekst telt niet mee). */
const SCORE_VELDEN: readonly ExtractieVeld[] = [
  'klant',
  'bv',
  'factuurnummer',
  'factuurdatum',
  'vervaldatum',
  'subtotaal',
  'btwBedrag',
  'btwPercentage',
  'totaal',
  'referentienummer',
  'regels',
]

/**
 * Aandeel velden met hoge zekerheid, als geheel percentage (0-100). Null als
 * er geen enkel veld gemeten is.
 */
export function berekenZekerheidScore(zekerheid: ExtractieZekerheidPerVeld): number | null {
  const gemeten = SCORE_VELDEN.filter((v) => zekerheid[v] !== undefined)
  if (gemeten.length === 0) return null
  const hoog = gemeten.filter((v) => zekerheid[v]?.zekerheid === 'high').length
  return Math.round((hoog / gemeten.length) * 100)
}

// ─── Regels: leesbare schrijfwijze herstellen ────────────────────────────────

function streepjesAaneen(waarde: string): string {
  // Kerning zet een spatie NA een streepje ("all- in", "2- ploegendienst").
  return waarde.replace(/(\S)-\s+(?=\S)/g, '$1-')
}

function leesbaar(ruw: string | null, woorden: readonly string[]): string | null {
  if (ruw === null) return null
  const kaal = normaliseerWitruimte(ruw)
  if (kaal.length === 0) return null
  return herstelSpaties(plet(kaal), woorden) ?? streepjesAaneen(kaal)
}

function leesbareNaam(ruw: string | null, woorden: readonly string[]): string | null {
  if (ruw === null) return null
  const kaal = normaliseerWitruimte(ruw)
  if (kaal.length === 0) return null
  return herstelSpaties(plet(kaal), woorden) ?? ontkernNaam(kaal)
}

function nlGetal(waarde: number, decimalen: number): string {
  return new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: decimalen,
    maximumFractionDigits: decimalen,
  }).format(waarde)
}

function pauzeTekst(minuten: number | null): string {
  if (minuten === null) return ''
  if (minuten === 0) return 'geen pauze'
  return `${Math.floor(minuten / 60)}:${String(minuten % 60).padStart(2, '0')} pauze`
}

/** De omschrijving opnieuw opbouwen uit de onderdelen (als de schrijfwijze niet te herstellen is). */
function bouwOmschrijving(r: FinqleTekstRegel, medewerker: string | null, tarieftype: string | null, dienst: string | null): string | null {
  if (r.soort === 'UREN' && r.datum && r.startTijd && r.eindTijd) {
    const pauze = pauzeTekst(r.pauzeMinuten)
    return [
      `Gewerkte uren ${medewerker ?? ''} ${isoNaarNlDatum(r.datum)} ${r.startTijd}-${r.eindTijd}`.replace(/\s+/g, ' ').trim(),
      pauze ? `(${pauze})` : '',
      tarieftype ? `- ${tarieftype}` : '',
    ]
      .filter((d) => d.length > 0)
      .join(' ')
  }
  if (r.soort === 'DAGTARIEF' && r.datum && r.werkbonNummer) {
    const dagen = r.eenhedenDuizendsten !== null ? duizendstenNaarGetal(r.eenhedenDuizendsten) : null
    const periode = r.datumTot
      ? `${isoNaarNlDatum(r.datum)} t/m ${isoNaarNlDatum(r.datumTot)}`
      : isoNaarNlDatum(r.datum)
    return [
      r.werkbonNummer,
      tarieftype,
      medewerker,
      dienst,
      dagen !== null ? `${nlGetal(dagen, 0)} ${dagen === 1 ? 'dag' : 'dagen'} (${periode})` : `(${periode})`,
    ]
      .filter((d): d is string => !!d && d.length > 0)
      .join(' - ')
  }
  if (r.soort === 'KOSTEN' && r.datum && r.eenheid === 'km' && r.eenhedenDuizendsten !== null && r.tariefCenten !== null) {
    const km = nlGetal(duizendstenNaarGetal(r.eenhedenDuizendsten), 0)
    return `${isoNaarNlDatum(r.datum)} ${r.werkbonNummer ? `${r.werkbonNummer} - ` : ''}Kilometervergoeding ${medewerker ?? ''}: ${km} km x € ${nlGetal(centenNaarGetal(r.tariefCenten), 2)}`
      .replace(/\s+/g, ' ')
      .trim()
  }
  return null
}

function naarGestructureerdeRegel(r: FinqleTekstRegel, woorden: readonly string[]): GestructureerdeFactuurRegel {
  const medewerker = leesbareNaam(r.medewerkerRuw, woorden)
  const tarieftype = leesbaar(r.tarieftypeRuw, woorden)
  const dienst = leesbaar(r.dienstRuw, woorden)
  // Eerst de letterlijke omschrijving in de schrijfwijze van de woordenlijst;
  // lukt dat niet, dan opnieuw opgebouwd uit de onderdelen; anders de ruwe tekst.
  const letterlijk = herstelSpaties(r.omschrijvingPlat, woorden)
  const kostenDatum = r.soort === 'KOSTEN' && r.datum ? `${isoNaarNlDatum(r.datum)} ` : ''
  const omschrijving =
    (letterlijk !== null ? `${kostenDatum}${letterlijk}` : null) ??
    bouwOmschrijving(r, medewerker, tarieftype, dienst) ??
    `${kostenDatum}${streepjesAaneen(normaliseerWitruimte(r.omschrijvingRuw))}`
  return {
    soort: r.soort,
    omschrijving,
    medewerker,
    datum: r.datum,
    datumTot: r.datumTot,
    startTijd: r.startTijd,
    eindTijd: r.eindTijd,
    pauzeMinuten: r.pauzeMinuten,
    tarieftype,
    dienst,
    werkbonNummer: r.werkbonNummer,
    eenheden: r.eenhedenDuizendsten !== null ? duizendstenNaarGetal(r.eenhedenDuizendsten) : null,
    eenheid: r.eenheid,
    tarief: r.tariefCenten !== null ? centenNaarGetal(r.tariefCenten) : null,
    totaalEx: centenNaarGetal(r.totaalExCenten),
    btwPercentage: r.btwPercentage,
    btwBedrag: r.btwCenten !== null ? centenNaarGetal(r.btwCenten) : null,
    totaalIncl: r.totaalInclCenten !== null ? centenNaarGetal(r.totaalInclCenten) : null,
  }
}

/** Gestructureerde regel -> extractieregel (aantal x prijs = totaal). */
export function naarExtractieRegel(r: GestructureerdeFactuurRegel): ExtractieRegelBasis {
  const metTarief = r.eenheden !== null && r.tarief !== null
  return {
    omschrijving: r.omschrijving,
    aantal: metTarief ? r.eenheden : 1,
    prijsPerStuk: metTarief ? r.tarief : r.totaalEx,
    totaal: r.totaalEx,
    btwPercentage: r.btwPercentage,
  }
}

function regelsSomCenten(regels: readonly ExtractieRegelBasis[] | null): number | null {
  if (!regels || regels.length === 0) return null
  return regels.reduce((s, r) => s + getalNaarCenten(r.totaal ?? 0), 0)
}

// ─── De naverwerking zelf ────────────────────────────────────────────────────

/**
 * Leg de PDF-tekst naast de AI-extractie. Zonder naverwerker of zonder tekst
 * blijven de AI-velden staan (alleen de IBAN-blokkade en "N/A" gelden altijd).
 */
export function pasNaverwerkingToe<T extends ExtractieVeldenBasis>(
  ai: T,
  opties: NaverwerkingOpties,
): NaverwerkingUitkomst<T> {
  const ingesteld = opties.factoringIbans ?? []
  const basis: T = {
    ...ai,
    crediteurBtwNummer: schoonBtwNummer(ai.crediteurBtwNummer),
    ...(ai.debiteurBtwNummer !== undefined ? { debiteurBtwNummer: schoonBtwNummer(ai.debiteurBtwNummer) } : {}),
  }

  if (opties.naverwerker !== 'FINQLE' || opties.tekst === null || opties.tekst.trim().length === 0) {
    const blok = pasIbanBlokkadeToe(basis, ingesteld)
    return {
      velden: blok.velden,
      zekerheid: zekerheidZonderTekst(blok.velden),
      regels: [],
      markeringen: [],
      geblokkeerdeIbans: blok.geblokkeerdeIbans,
      factoringIbanInTekst: null,
      debiteur: null,
      afzender: null,
      besteldDoor: null,
      tekstGelezen: false,
    }
  }

  const t: FinqleTekstExtractie = leesFinqleTekst(opties.tekst, { regelParsers: opties.regelParsers })
  // Geen kop en geen totalen gevonden: de tekst is toch niet deze opmaak.
  const tekstGelezen = t.factuurnummer !== null || t.totaalTeBetalenCenten !== null || t.regels.length > 0
  const markeringen: string[] = []
  const zekerheid: ExtractieZekerheidPerVeld = {}

  // ── Kop ──
  const nummer = kiesTekst(ai.factuurnummer, t.factuurnummer, false)
  zekerheid.factuurnummer = nummer.zekerheid
  const datum = kiesTekst(ai.datum, t.factuurdatum, false)
  zekerheid.factuurdatum = datum.zekerheid
  const verval = kiesTekst(ai.vervaldatum, t.vervaldatum, false)
  zekerheid.vervaldatum = verval.zekerheid

  // Woordenlijst voor de leesbare schrijfwijze (kerning): de woorden van de AI
  // en de namen van medewerkers.
  const woorden = woordenUit([
    ...(ai.regels ?? []).map((r) => r.omschrijving),
    ai.omschrijving,
    ai.referentienummer,
    ...(opties.bekendeNamen ?? []),
  ])

  // "PO:" is de referentie. Staat er geen PO maar wel een project, dan is het
  // project de referentie van deze factuur (met een lagere zekerheid: het is
  // niet het veld waar de klant zijn ordernummer zet).
  let referentie = kiesTekst(ai.referentienummer, t.po, t.poAanwezig)
  if (t.po === null && t.project !== null) {
    const zelfde = gelijkeTekst(ai.referentienummer, t.project)
    referentie = {
      waarde: zelfde ? ai.referentienummer : (leesbaar(t.project, woorden) ?? t.project),
      zekerheid: {
        zekerheid: 'medium',
        bron: zelfde ? 'TEKST_EN_AI' : 'TEKST',
        ...(ai.referentienummer !== null && !zelfde ? { aiWeekAf: true } : {}),
        toelichting: 'Er staat geen PO op de factuur; het project is als referentie overgenomen.',
      },
    }
  }
  zekerheid.referentienummer = referentie.zekerheid

  // ── Totalen: uit de "Totaal te betalen"-tabel, met consistentiecheck ──
  const eind = finqleEindtotalen(t)
  const totalenBetrouwbaar = t.consistentie.totalenSluiten === true && t.consistentie.totaalTeBetalenKlopt !== false
  // Een creditfactuur: zegt de AI "negatief" en de tekst "positief" (of andersom),
  // dan wint geen van beide stil. Zie `kiesBedrag`.
  const tekenOnzeker =
    tekenVerschilt(ai.subtotaal, eind.exCenten) ||
    tekenVerschilt(ai.btwBedrag, eind.btwCenten) ||
    tekenVerschilt(ai.totaal, eind.inclCenten)
  const tekstIsNegatief = (eind.exCenten ?? 0) < 0 || (eind.inclCenten ?? 0) < 0
  const subtotaal = kiesBedrag(ai.subtotaal, eind.exCenten, totalenBetrouwbaar, tekenOnzeker)
  const btwBedrag = kiesBedrag(ai.btwBedrag, eind.btwCenten, totalenBetrouwbaar, tekenOnzeker)
  const totaal = kiesBedrag(ai.totaal, eind.inclCenten, totalenBetrouwbaar, tekenOnzeker)
  zekerheid.subtotaal = subtotaal.zekerheid
  zekerheid.btwBedrag = btwBedrag.zekerheid
  zekerheid.totaal = totaal.zekerheid
  if (tekenOnzeker) {
    markeringen.push(
      'De PDF-tekst en de AI-uitlezing verschillen in het teken van de bedragen (mogelijk een creditfactuur). De bedragen zijn als negatief overgenomen - controleer of dat klopt.',
    )
  }
  if (t.consistentie.totalenSluiten === false || t.consistentie.totaalTeBetalenKlopt === false) {
    markeringen.push(
      'De totalen in de PDF-tekst sluiten niet (excl + btw is niet gelijk aan het totaal te betalen) - controleer de bedragen.',
    )
  }

  let btwPercentage = ai.btwPercentage
  if (t.btwRijen.length === 1) {
    const pct = t.btwRijen[0]?.percentage ?? null
    zekerheid.btwPercentage = {
      zekerheid: 'high',
      bron: pct !== null && ai.btwPercentage === pct ? 'TEKST_EN_AI' : 'TEKST',
      ...(ai.btwPercentage !== null && ai.btwPercentage !== pct ? { aiWeekAf: true } : {}),
    }
    btwPercentage = pct
  } else if (t.btwRijen.length > 1) {
    // Meerdere tarieven: het veld draagt het tarief met de grootste grondslag.
    const grootste = [...t.btwRijen].sort((a, b) => Math.abs(b.grondslagCenten) - Math.abs(a.grondslagCenten))[0]
    btwPercentage = grootste?.percentage ?? ai.btwPercentage
    zekerheid.btwPercentage = {
      zekerheid: 'medium',
      bron: 'TEKST',
      toelichting: 'Meerdere btw-tarieven op de factuur.',
    }
    markeringen.push(
      `Meerdere btw-tarieven op de factuur (${t.btwRijen.map((r) => `${nlGetal(r.percentage, 0)}%`).join(', ')}) - controleer het btw-percentage en het btw-bedrag.`,
    )
  } else {
    zekerheid.btwPercentage =
      ai.btwPercentage !== null ? { zekerheid: 'medium', bron: 'AI' } : { zekerheid: 'low', bron: 'GEEN' }
  }

  // ── Regels ──
  // De regels uit de tekst tellen alleen als ze sluiten EN het teken van de
  // bedragen vaststaat. Draaide het teken om (de tekst toont positieve bedragen,
  // de AI leest een creditfactuur), dan passen de tekstregels niet meer bij de
  // totalen: de regels van de AI blijven staan en er wordt niets gekoppeld.
  const tekstRegelsBruikbaar = !tekenOnzeker || tekstIsNegatief
  let regels: ExtractieRegelBasis[] | null = ai.regels
  let gestructureerd: GestructureerdeFactuurRegel[] = []
  if (t.regels.length > 0 && t.consistentie.regelsSluiten === true && tekstRegelsBruikbaar) {
    gestructureerd = t.regels.map((r) => naarGestructureerdeRegel(r, woorden))
    regels = gestructureerd.map(naarExtractieRegel)
    const aiSom = regelsSomCenten(ai.regels)
    const zelfdeAantal = (ai.regels?.length ?? 0) === t.regels.length
    zekerheid.regels = {
      zekerheid: t.consistentie.regelBedragenKloppen === false ? 'medium' : 'high',
      bron: zelfdeAantal && aiSom === eind.exCenten ? 'TEKST_EN_AI' : 'TEKST',
      ...(ai.regels !== null && !(zelfdeAantal && aiSom === eind.exCenten) ? { aiWeekAf: true } : {}),
    }
  } else {
    if (t.regels.length > 0 && t.consistentie.regelsSluiten !== true) {
      markeringen.push(
        'De factuurregels in de PDF-tekst tellen niet op tot het bedrag excl. btw - controleer de regels.',
      )
    }
    const aiSom = regelsSomCenten(ai.regels)
    const doel = subtotaal.waarde !== null ? getalNaarCenten(subtotaal.waarde) : eind.exCenten
    zekerheid.regels =
      ai.regels === null
        ? { zekerheid: 'low', bron: 'GEEN' }
        : { zekerheid: aiSom !== null && aiSom === doel ? 'medium' : 'low', bron: 'AI' }
  }

  zekerheid.omschrijving =
    ai.omschrijving !== null ? { zekerheid: 'medium', bron: 'AI' } : { zekerheid: 'low', bron: 'GEEN' }

  // ── Partijen: "Debiteur" is de klant, het afzenderblok de BV ──
  const debiteur: NaverwerktePartij | null = t.debiteur
    ? { naam: t.debiteur.naam, kvk: t.debiteur.kvk, btwNummer: t.debiteur.btwNummer }
    : null
  const afzender: NaverwerktePartij | null = t.afzender
    ? { naam: t.afzender.naam, kvk: t.afzender.kvk, btwNummer: t.afzender.btwNummer }
    : null

  const velden: T = {
    ...basis,
    factuurnummer: nummer.waarde,
    datum: datum.waarde,
    vervaldatum: verval.waarde,
    referentienummer: referentie.waarde,
    subtotaal: subtotaal.waarde,
    btwBedrag: btwBedrag.waarde,
    totaal: totaal.waarde,
    btwPercentage,
    regels,
    ...(debiteur
      ? {
          debiteurnaam: debiteur.naam ?? basis.debiteurnaam,
          debiteurKvk: debiteur.kvk ?? basis.debiteurKvk,
          // Het blok is gevonden: een leeg btw-nummer ("N/A") is dan een feit.
          debiteurBtwNummer: debiteur.btwNummer,
        }
      : {}),
    ...(afzender
      ? {
          bvNaam: afzender.naam ?? basis.bvNaam,
          crediteurNaam: afzender.naam ?? basis.crediteurNaam,
          crediteurKvk: afzender.kvk ?? basis.crediteurKvk,
          crediteurBtwNummer: afzender.btwNummer ?? basis.crediteurBtwNummer,
        }
      : {}),
  }

  // ── IBAN-blokkade: ingesteld + het IBAN uit de betalingsinstructies ──
  const factoringIbanInTekst: GeblokkeerdIban | null = t.factoringIban
    ? { iban: t.factoringIban, tenNameVan: t.factoringTenNameVan }
    : null
  const blok = pasIbanBlokkadeToe(velden, [...ingesteld, ...(factoringIbanInTekst ? [factoringIbanInTekst] : [])])

  return {
    velden: blok.velden,
    zekerheid: tekstGelezen ? zekerheid : zekerheidZonderTekst(blok.velden),
    regels: gestructureerd,
    markeringen,
    geblokkeerdeIbans: blok.geblokkeerdeIbans,
    factoringIbanInTekst,
    debiteur,
    afzender,
    besteldDoor: t.besteldDoor?.naam ?? null,
    tekstGelezen,
  }
}
